import { useState, useCallback, useMemo } from 'react';
import type { Path, PathValue, FieldValues } from 'react-hook-form';
import type { UseMapFormSyncOptions, UseMapFormSyncReturn } from 'src/types/map-hooks';
import type { LatLngLiteral, AutocompleteResult } from 'src/types/map-provider';

/**
 * Hook for synchronizing map interactions with form state
 *
 * This hook manages the synchronization between map user interactions (place selection,
 * marker dragging) and react-hook-form state. It owns the current address and search
 * value state, and provides callbacks that update both the form and local state.
 *
 * @example
 * ```tsx
 * const {
 *   currentAddress,
 *   searchValue,
 *   setSearchValue,
 *   updateAddressFromLocation,
 *   handlePlaceSelect,
 *   handleMarkerDragEnd
 * } = useMapFormSync({
 *   provider,
 *   mapInstance,
 *   reverseGeocode,
 *   methods,
 *   setAddressInfo,
 *   onChange,
 * });
 * ```
 *
 * @param options - Configuration options
 * @returns Form sync state and callback actions
 */
export function useMapFormSync<T extends FieldValues = FieldValues>(
  options: UseMapFormSyncOptions
): UseMapFormSyncReturn {
  const {
    provider,
    mapInstance,
    reverseGeocode,
    methods,
    setAddressInfo,
    onChange,
    onMarkerDragEnd,
    onMarkersUpdate,
    initialAddress = '',
    viewOnly = false,
    t,
  } = options;

  // State for current address and search value
  const [currentAddress, setCurrentAddress] = useState<string>(
    methods?.getValues('address' as Path<T>) || initialAddress
  );
  const [searchValue, setSearchValue] = useState('');

  /**
   * Update address from a geographic location via reverse geocoding
   * Updates both form values and local state
   */
  const updateAddressFromLocation = useCallback(
    async (position: LatLngLiteral, skipFormUpdate = false): Promise<boolean> => {
      if (!provider) return false;

      try {
        const result = await reverseGeocode(position);

        if (result) {
          if (!skipFormUpdate && methods) {
            methods.setValue(
              'location' as Path<T>,
              `${position.lat},${position.lng}` as PathValue<T, Path<T>>,
              { shouldValidate: true, shouldDirty: true, shouldTouch: true }
            );

            methods.setValue(
              'address' as Path<T>,
              result.formattedAddress as PathValue<T, Path<T>>,
              { shouldValidate: true, shouldDirty: true, shouldTouch: true }
            );
          }

          setCurrentAddress(result.formattedAddress);
          setAddressInfo?.(result.addressInfo);
          onChange?.(result.formattedAddress);

          return true;
        }
      } catch (error) {
        // Silent error handling - geocoding failure
      }

      return false;
    },
    [provider, reverseGeocode, methods, setAddressInfo, onChange]
  );

  /**
   * Handle place selection from autocomplete
   * Centers map and updates form with selected place
   */
  const handlePlaceSelect = useCallback(
    async (result: AutocompleteResult | null) => {
      if (!result?.geometry?.location || !mapInstance) return;

      const position = result.geometry.location;

      try {
        // Center map on selected place
        mapInstance.setCenter(position);
        mapInstance.setZoom(17);

        // Update form values
        const formattedAddress = result.formattedAddress || result.description || '';
        setCurrentAddress(formattedAddress);
        setSearchValue('');

        if (methods) {
          methods.setValue(
            'location' as Path<T>,
            `${position.lat},${position.lng}` as PathValue<T, Path<T>>,
            { shouldValidate: true, shouldDirty: true, shouldTouch: true }
          );

          methods.setValue(
            'address' as Path<T>,
            formattedAddress as PathValue<T, Path<T>>,
            { shouldValidate: true, shouldDirty: true, shouldTouch: true }
          );
        }

        if (result.addressComponents && setAddressInfo && provider) {
          const addressInfo = provider.parseAddressComponents(result.addressComponents);
          setAddressInfo(addressInfo);
        }

        onChange?.(formattedAddress);

        // Create marker at selected location
        if (onMarkersUpdate) {
          const newMarker = {
            position,
            draggable: !viewOnly,
            title: t?.('dragToUpdate') || 'Drag to update location',
          };
          onMarkersUpdate([newMarker]);
        }
      } catch (error) {
        // Silent error handling - place selection failure
      }
    },
    [mapInstance, methods, setAddressInfo, onChange, provider, onMarkersUpdate, viewOnly, t]
  );

  /**
   * Handle marker drag end event
   * Updates address from new marker position
   */
  const handleMarkerDragEnd = useCallback(
    async (index: number, position: LatLngLiteral) => {
      try {
        const success = await updateAddressFromLocation(position);
        if (success) {
          const result = await reverseGeocode(position);
          onMarkerDragEnd?.(index, position, result?.formattedAddress, result?.addressInfo);
        }
      } catch (error) {
        // Silent error handling - drag end failure
      }
    },
    [updateAddressFromLocation, reverseGeocode, onMarkerDragEnd]
  );

  // Return memoized value
  const memoizedValue = useMemo<UseMapFormSyncReturn>(
    () => ({
      currentAddress,
      searchValue,
      setCurrentAddress,
      setSearchValue,
      updateAddressFromLocation,
      handlePlaceSelect,
      handleMarkerDragEnd,
    }),
    [
      currentAddress,
      searchValue,
      updateAddressFromLocation,
      handlePlaceSelect,
      handleMarkerDragEnd,
    ]
  );

  return memoizedValue;
}
